import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { MotionPlugin } from '@vueuse/motion'
import App from './App.vue'
import router from './router'
import { i18n } from './i18n'
import { requirePaidDirective } from './directives/requirePaid'
import { getStoredAppLocale } from './config/app-locales'
import { getBootstrapLocale } from '@/utils/landingBrowserLocale'
import { installGa4ConsentShell, initGa4RouterHooks } from '@/utils/gtagBootstrap'
import { initSentry, captureSentryException } from '@/utils/sentryBootstrap'
import {
  clearStaleChunkReloadMarker,
  isStaleChunkLoadError,
  reloadOnceForStaleChunk,
} from '@/utils/chunkLoadRecovery'
import './assets/main.css'

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const r = event.reason
    if (isStaleChunkLoadError(r) && reloadOnceForStaleChunk(r)) {
      event.preventDefault()
    }
  })
}

installGa4ConsentShell()

// Main app UI locale: explicit user choice (localStorage), else bootstrap: marketing / on main host → Spanish if browser language is es*, else English; other routes use VITE_DEFAULT_LOCALE.
const storedLocale = getStoredAppLocale()
const appLocale = storedLocale ?? getBootstrapLocale(storedLocale)
i18n.global.locale.value = appLocale
document.documentElement.dir = appLocale === 'ar' ? 'rtl' : 'ltr'

// Single entry for both SPA and Blade-served public page. When Laravel serves the page at
// {slug}.RESTAURANT_DOMAIN, the Blade view outputs #app with data-restaurant-slug and data-locale.
// The same Vue app mounts here; RootView shows PublicRestaurantView when the host is a restaurant
// subdomain (getSubdomainSlug()). Slug comes from the hostname; locale from #app's data-locale when present.

initGa4RouterHooks(router)

function teardownStaticMarketingShell() {
  if (typeof document === 'undefined') return
  try {
    document.documentElement.removeAttribute('data-rms-static-landing')
    document.getElementById('rms-static-marketing-fallback')?.remove()
  } catch {
    /* ignore */
  }
}

try {
  const app = createApp(App)
  initSentry(app, router)
  app.config.errorHandler = (err, instance, info) => {
    if (err && isStaleChunkLoadError(err) && reloadOnceForStaleChunk(err)) return
    console.error(err)
    captureSentryException(err, { contexts: { vue: { info } } })
  }
  app.use(createPinia())
  app.use(router)
  app.use(i18n)
  app.use(MotionPlugin)
  requirePaidDirective(app)
  app.mount('#app')
  clearStaleChunkReloadMarker()
} catch (err) {
  console.error('[rms] Vue bootstrap failed', err)
  captureSentryException(err)
  const root = document.getElementById('app')
  if (root && !root.querySelector('[data-rms-bootstrap-fallback]')) {
    root.innerHTML =
      '<div data-rms-bootstrap-fallback style="padding:1.25rem;max-width:32rem;margin:2rem auto;font-family:system-ui,sans-serif;line-height:1.5;color:#0f172a">' +
      '<p style="font-weight:700;margin:0 0 0.5rem">This page could not load the app.</p>' +
      '<p style="margin:0 0 0.75rem;color:#475569">Ad blockers and strict privacy extensions sometimes block scripts by filename. Try allowing this site or turning the extension off for it, then hard-refresh (Ctrl+Shift+R or Cmd+Shift+R).</p>' +
      '<p style="margin:0;font-size:0.875rem;color:#64748b">Open the browser console (F12) for details.</p>' +
      '</div>'
  }
} finally {
  teardownStaticMarketingShell()
}
